import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Flex,
  Input,
  PlusIcon,
  SearchIcon,
  useDebouncedValue,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useHasOrganizationAction,
  useOrganizationId,
  useOrganizationMembersTotal,
} from '~publish/contexts/AccountContext'

import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { EditMemberDialog } from './components/EditMemberDialog'
import { NativeConfirmRemoveMemberDialog } from './components/NativeConfirmRemoveMemberDialog'
import { RolesInviteMemberDialog } from './components/RolesInviteMemberDialog'
import { TeamHeaderMenu } from './components/TeamHeaderMenu'
import { TeamMembersFilterMenu } from './components/TeamMembersFilterMenu'
import { TeamMembersTable } from './components/TeamMembersTable'
import { TeamSummaryCards } from './components/TeamSummaryCards'
import { MEMBER_SEARCH_DEBOUNCE_MS, MEMBER_SEARCH_THRESHOLD } from './constants'
import {
  useAssignableChannels,
  useTeamMemberCounts,
  useTeamMembers,
} from './hooks'
import type { TeamMember } from './hooks'
import {
  EMPTY_TEAM_MEMBERS_FILTERS,
  toTeamMembersFilterInput,
} from './utils/teamMembersFilters'
import type { TeamMembersFilters } from './utils/teamMembersFilters'

import styles from './RolesTeam.module.css'

/**
 * Team Members page for organizations on the Roles model.
 *
 * Sits on an 800px base layout on desktop, wider than the 600px settings
 * default, so the member table has room for its columns.
 */
export const RolesTeam = (): JSX.Element => {
  const { account } = useAccount()
  const canViewTeamMembers = useHasOrganizationAction('viewTeamMembers')
  const canManageTeamMembers = useHasOrganizationAction('manageTeamMembers')
  const organizationId = useOrganizationId()
  const memberCount = useOrganizationMembersTotal() ?? 0
  const organizationName = account?.currentOrganization?.name ?? 'your team'

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    MEMBER_SEARCH_DEBOUNCE_MS,
  )
  const canSearchMembers =
    canViewTeamMembers && memberCount > MEMBER_SEARCH_THRESHOLD
  const search = canSearchMembers ? debouncedSearchQuery : ''

  // Shares the search's toolbar, so it only applies while that is shown.
  const [filterState, setFilterState] = useState<TeamMembersFilters>(
    EMPTY_TEAM_MEMBERS_FILTERS,
  )
  const filters = canSearchMembers ? filterState : EMPTY_TEAM_MEMBERS_FILTERS
  const filterInput = useMemo(
    () => toTeamMembersFilterInput(filters),
    [filters],
  )

  const teamMemberCounts = useTeamMemberCounts({ enabled: canViewTeamMembers })
  const membersQuery = useTeamMembers({
    organizationId: organizationId ?? '',
    search,
    filter: filterInput,
    enabled: filters.status !== 'pending',
  })
  // Same variables as the table's call; Apollo dedupes the in-flight requests.
  const { assignableChannels } = useAssignableChannels({
    organizationId: organizationId ?? '',
  })

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null)

  // Server-computed: manageTeamMembers already folds in role, lock state and
  // email verification, so there is no separate verified-email variant here.
  const headerActions = (
    <>
      {canManageTeamMembers && (
        <Button onClick={(): void => setIsInviteDialogOpen(true)}>
          <PlusIcon />
          Invite Team Member
        </Button>
      )}
      <TeamHeaderMenu />
    </>
  )

  return (
    <>
      <NewSettingsPageLayout
        title="Team Members"
        subtitle={`Invite people to ${organizationName} and control what they can do.`}
        actions={headerActions}
        actionsAlign="start"
        inlineActionsOnMobile
        className={styles.layout}
      >
        <Flex direction="column" fullWidth className={styles.content}>
          <TeamSummaryCards counts={teamMemberCounts} />
          <Flex direction="column" gap="sm" fullWidth>
            {canSearchMembers && (
              <Flex align="center" justify="between" gap="sm" fullWidth>
                <Input
                  prefix={<SearchIcon />}
                  placeholder="Search members"
                  aria-label="Search team members"
                  value={searchQuery}
                  onChange={(e): void => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                <TeamMembersFilterMenu
                  filters={filters}
                  onFiltersChange={setFilterState}
                  channels={assignableChannels ?? []}
                />
              </Flex>
            )}
            <TeamMembersTable
              members={membersQuery}
              search={search}
              filters={filters}
              onClearFilters={(): void =>
                setFilterState(EMPTY_TEAM_MEMBERS_FILTERS)
              }
              onEdit={setEditingMember}
              onRemove={setRemovingMember}
            />
          </Flex>
        </Flex>
      </NewSettingsPageLayout>

      <RolesInviteMemberDialog
        isOpen={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onClose={(): void => setIsInviteDialogOpen(false)}
      />

      <EditMemberDialog
        isOpen={editingMember !== null}
        onOpenChange={(open): void => {
          if (!open) setEditingMember(null)
        }}
        onClose={(): void => setEditingMember(null)}
        member={editingMember}
      />

      <NativeConfirmRemoveMemberDialog
        isOpen={removingMember !== null}
        onOpenChange={(open): void => {
          if (!open) setRemovingMember(null)
        }}
        onClose={(): void => setRemovingMember(null)}
        member={removingMember}
      />
    </>
  )
}
